import { useEffect, useRef, useState, useCallback } from "react";
import { getWsBackendUrl } from "../lib/utils";

/**
 * useSessionChannel — bidirectional session relay used by the gaseous deck.
 *
 * Combines two transports:
 *   1. BroadcastChannel "sparkl-classroom-session" — same-browser fan-out.
 *      Carries everything the bridge (sparkl-bridge.js) already broadcasts.
 *   2. WebSocket /api/sessions/{sessionId}/ws — cross-device relay so the
 *      mobile RN controller (or a teacher on a different laptop) joins the
 *      same room.
 *
 * Messages are forwarded between the two transports so any device — same
 * tab, same browser, or a phone halfway across the room — sees the same
 * stream. Echoes are deduped via a 200ms `_relayId` ring.
 *
 * Returns:
 *   { connected, send(msg), onMessage(fn) }
 */
const CHANNEL_NAME = "sparkl-classroom-session";
const DEDUPE_WINDOW_MS = 800;

const wsBaseFromEnv = () => {
    return getWsBackendUrl();
};

export const useSessionChannel = (sessionId) => {
    const [connected, setConnected] = useState(false);
    const bcRef = useRef(null);
    const wsRef = useRef(null);
    const listenersRef = useRef(new Set());
    const seenIdsRef = useRef(new Map()); // relayId -> expireAt
    const reconnectTimerRef = useRef(null);

    const dedupe = useCallback((msg) => {
        const id = msg?._relayId;
        if (!id) return false;
        const seen = seenIdsRef.current;
        const now = Date.now();
        // Garbage-collect old entries.
        for (const [k, exp] of seen) {
            if (exp < now) seen.delete(k);
        }
        if (seen.has(id)) return true;
        seen.set(id, now + DEDUPE_WINDOW_MS);
        return false;
    }, []);

    /* ---- BroadcastChannel ---- */
    useEffect(() => {
        if (!sessionId) return;
        let bc;
        try {
            bc = new BroadcastChannel(CHANNEL_NAME);
        } catch {
            return;
        }
        bcRef.current = bc;
        bc.onmessage = (e) => {
            const msg = e?.data;
            if (!msg || msg.sessionId !== sessionId) return;
            if (dedupe(msg)) {
                // We've seen this id already (or will see it). Still forward
                // to listeners on first sight, but skip the WS re-broadcast
                // below if it's a true duplicate.
            }
            // Forward BC traffic out to WS so cross-device peers also hear it.
            // If WS isn't open right now, push onto the same outbox so the
            // message ships once we reconnect (otherwise BC-only commands
            // would silently fail to reach the smartboard during reconnect).
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send(JSON.stringify(msg));
                } catch {
                    outboxRef.current.push(msg);
                }
            } else {
                const q = outboxRef.current;
                q.push(msg);
                if (q.length > MAX_QUEUE) q.shift();
            }
            // Deliver locally.
            for (const fn of listenersRef.current) {
                try {
                    fn(msg);
                } catch {
                    /* noop */
                }
            }
        };
        return () => {
            try {
                bc.close();
            } catch {
                /* noop */
            }
            bcRef.current = null;
        };
    }, [sessionId, dedupe]);

    /* ---- WebSocket ---- */
    useEffect(() => {
        if (!sessionId) return;
        const wsBase = wsBaseFromEnv();
        if (!wsBase) return; // No backend URL — WS optional, BC still works.
        let cancelled = false;        const open = () => {
            if (cancelled) return;
            let ws;
            try {
                ws = new WebSocket(
                    `${wsBase}/axis-ai/v1/octane/sessions/${encodeURIComponent(sessionId)}/ws`,
                );
            } catch {
                scheduleReconnect();
                return;
            }
            wsRef.current = ws;
            ws.onopen = () => {
                setConnected(true);
                flushOutbox();
            };
            ws.onclose = () => {
                setConnected(false);
                wsRef.current = null;
                scheduleReconnect();
            };
            ws.onerror = () => {
                try {
                    ws.close();
                } catch {
                    /* noop */
                }
            };
            ws.onmessage = (e) => {
                let msg;
                try {
                    msg = JSON.parse(e.data);
                } catch {
                    return;
                }
                if (msg?.type === "HELLO") return;
                if (!msg || msg.sessionId !== sessionId) return;
                if (dedupe(msg)) return;
                // Forward WS message into the BroadcastChannel so the deck
                // iframe (which only speaks BC) hears it too.
                try {
                    bcRef.current?.postMessage(msg);
                } catch {
                    /* noop */
                }
                for (const fn of listenersRef.current) {
                    try {
                        fn(msg);
                    } catch {
                        /* noop */
                    }
                }
            };
        };

        const scheduleReconnect = () => {
            if (cancelled) return;
            if (reconnectTimerRef.current) return;
            reconnectTimerRef.current = setTimeout(() => {
                reconnectTimerRef.current = null;
                open();
            }, 1500);
        };

        open();

        return () => {
            cancelled = true;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            try {
                wsRef.current?.close();
            } catch {
                /* noop */
            }
            wsRef.current = null;
        };
    }, [sessionId, dedupe]);

    /* ---- send ---- */
    const outboxRef = useRef([]);
    const MAX_QUEUE = 30;

    const flushOutbox = useCallback(() => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        const pending = outboxRef.current;
        outboxRef.current = [];
        for (const msg of pending) {
            try {
                ws.send(JSON.stringify(msg));
            } catch {
                outboxRef.current.push(msg);
            }
        }
    }, []);

    const send = useCallback(
        (msg) => {
            if (!msg || !sessionId) return;
            const enriched = {
                ...msg,
                sessionId,
                _relayId:
                    msg._relayId ||
                    `${Date.now().toString(36)}-${Math.random()
                        .toString(36)
                        .slice(2, 8)}`,
            };
            // Pre-seed the dedupe set so our own echo doesn't loop back.
            dedupe(enriched);
            try {
                bcRef.current?.postMessage(enriched);
            } catch {
                /* noop */
            }
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send(JSON.stringify(enriched));
                    return;
                } catch {
                    /* fall through to queue */
                }
            }
            // Socket isn't open — buffer until reconnect. Without this
            // queue, commands fired during the 1.5s reconnect window
            // (network glitch, tab backgrounded) silently vanished,
            // leaving the smartboard stuck on the previous slide.
            const q = outboxRef.current;
            q.push(enriched);
            if (q.length > MAX_QUEUE) q.shift();
        },
        [sessionId, dedupe],
    );

    const onMessage = useCallback((fn) => {
        listenersRef.current.add(fn);
        return () => {
            listenersRef.current.delete(fn);
        };
    }, []);

    return { connected, send, onMessage };
};

export default useSessionChannel;
